import { PDFDocument, degrees } from "pdf-lib";
import { toArrayBuffer } from "./pdf-bytes";

export interface PdfPageRotation {
  /** 1-based first page of the range, inclusive. */
  startPage: number;
  /** 1-based last page of the range, inclusive. */
  endPage: number;
  /** Clockwise degrees to add to what the pages already carry. */
  rotation: number;
}

/** Clockwise degrees snapped to a quarter turn, in [0, 360). */
export function normalizeRotation(rotation: number): number {
  return (((Math.round(rotation / 90) * 90) % 360) + 360) % 360;
}

/**
 * Turn page ranges of an existing PDF, leaving every other page untouched.
 * Rotation is additive, so a page that already came in sideways keeps that
 * tilt and gains the requested one.
 */
export async function rotatePdfPages(
  pdf: ArrayBuffer,
  rotations: PdfPageRotation[],
): Promise<ArrayBuffer> {
  const turns = rotations.filter(
    (entry) => normalizeRotation(entry.rotation) !== 0,
  );
  if (turns.length === 0) return pdf;

  const document = await PDFDocument.load(pdf, { ignoreEncryption: true });
  const pages = document.getPages();

  turns.forEach(({ startPage, endPage, rotation }) => {
    const from = Math.max(startPage, 1);
    const to = Math.min(endPage, pages.length);
    for (let page = from; page <= to; page += 1) {
      const target = pages[page - 1];
      if (!target) continue;
      target.setRotation(
        degrees(normalizeRotation(target.getRotation().angle + rotation)),
      );
    }
  });

  return toArrayBuffer(await document.save());
}
